import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Help as HelpIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Keyboard as KeyboardIcon,
  Category as CategoryIcon,
  Description as DescriptionIcon,
  Settings as SettingsIcon,
  FileCopy as FileCopyIcon,
  ContentCut as ContentCutIcon,
  ContentPaste as ContentPasteIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  DriveFileRenameOutline as DriveFileRenameIcon,
  CallSplit as CallSplitIcon,
  CallMerge as CallMergeIcon,
  JoinFull as JoinFullIcon,
  KeyboardDoubleArrowUp as KeyboardDoubleArrowUpIcon,
  AutoAwesome as AutoAwesomeIcon,
  Visibility as VisibilityIcon,
  FindInPage as FindInPageIcon,
  EditNote as EditNoteIcon,
  Group as GroupIcon,
  FileCopy as FileCopyIcon2,
  Info as InfoIcon,
} from '@mui/icons-material';
import { helpContent, HelpContentKey } from '../../constants/helpContent';
import api from '../../services/api';
import HelpScreenshot, { HelpScreenshot as HelpScreenshotType } from './HelpScreenshot';
import { HelpScreenshotService } from '../../services/HelpScreenshotService';

interface HelpDialogProps {
  open: boolean;
  onClose: () => void;
}

interface HelpMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

/** Sidebar topics under Help → Shortcuts & gestures (normalized keys). */
function getShortcutsSubtopicAnswer(trimmedLower: string): string | null {
  switch (trimmedLower) {
    case 'keyboard shortcuts':
      return (
        'Keyboard shortcuts (⌘ = Mac Cmd; Ctrl on Windows/Linux)\n\n' +
        'These apply when focus is not in a normal text field, multiline box, or the rich-text editor — so typing and editor shortcuts are not overridden:\n\n' +
        '• ⌘/Ctrl+Z — Undo\n' +
        '• ⌘/Ctrl+Shift+Z — Redo\n' +
        '• ⌘/Ctrl+X — Cut\n' +
        '• ⌘/Ctrl+C — Copy\n' +
        '• ⌘/Ctrl+V — Paste\n' +
        '• Delete — Delete selected category or content (opens confirmation when allowed). On Mac keyboards that send Backspace for the key labeled Delete, Backspace works the same here when focus is outside text fields.\n' +
        '• ↑ / ↓ — Move the selection up or down among siblings when focus is outside the category tree (same as toolbar move arrows).\n' +
        '• ⌘/Ctrl+↑ / ⌘/Ctrl+↓ — Same move, works even when the category tree has keyboard focus (still not while typing in fields or the editor).'
      );
    case 'editing shortcuts':
      return (
        'Editing shortcuts\n\n' +
        'Category tree — inline rename field:\n' +
        '• Enter — Confirm the new name\n' +
        '• Escape — Cancel and revert\n\n' +
        'Semantic search (AI) — question field:\n' +
        '• Enter — Send your question\n' +
        '• Shift+Enter — New line in the multiline field\n\n' +
        'Rich-text content editor:\n' +
        '• Standard shortcuts work inside the editor (for example ⌘/Ctrl+Z for undo there). Document-wide cut/copy/paste and undo above are disabled while focus is in the editor so they do not conflict with editing.'
      );
    case 'find shortcuts':
      return (
        'Find shortcuts (⌘ = Mac Cmd; Ctrl on Windows/Linux)\n\n' +
        'Open Find:\n' +
        '• ⌘/Ctrl+F — Opens the Find dialog when focus is not in a normal text field or multiline box.\n\n' +
        'While Find is open — these are skipped while you are typing in Find’s search fields:\n' +
        '• ⌘/Ctrl+F — Focus the Find search field and select its text\n' +
        '• Enter — Run / repeat search\n' +
        '• F3 or ⌘/Ctrl+G — Find next match\n' +
        '• Shift+F3 or ⌘/Ctrl+Shift+G — Find previous match\n' +
        '• Ctrl+Shift+F3 or ⌘/Ctrl+Shift+Alt+G — Jump to next category with matches\n' +
        '• Ctrl+Alt+F3 or ⌘/Ctrl+Alt+G — Jump to previous category with matches\n' +
        '• Escape — Close Find'
      );
    case 'navigation':
      return (
        'Navigation\n\n' +
        '• Click document tabs or rows in the document panel to switch documents.\n' +
        '• Click categories in the tree or rows in the content table to select them.\n' +
        '• Expand or collapse branches with the tree’s expand controls (and double-click the category label — see Mouse and gestures).\n' +
        '• Tab / Shift+Tab moves focus between controls like in most web apps.\n' +
        '• Use Find (⌘/Ctrl+F) or the search toolbar control to jump to matching categories and content.'
      );
    case 'mouse and gestures':
      return (
        'Mouse and gestures\n\n' +
        '• Click to select categories or content rows.\n' +
        '• ⌘/Ctrl+click — Add or remove an item from the selection (multi-select).\n' +
        '• Shift+click — Select a range among sibling categories or sibling content rows.\n' +
        '• Double-click a category label in the tree — Expand or collapse that category when it has children.\n' +
        '• Select a category, then click its label again after a short pause — Start inline rename.\n' +
        '• Right-click — Context menus on documents, tree items, and content rows.\n' +
        '• Drag and drop — Move categories or content where targets allow it; hold ⌥ Alt (Option on Mac) while dropping on the tree where supported to duplicate instead of move.'
      );
    default:
      return null;
  }
}

const HelpDialog: React.FC<HelpDialogProps> = ({ open, onClose }) => {
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState<HelpMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [useSemanticSearch, setUseSemanticSearch] = useState(true);
  const [showScreenshots, setShowScreenshots] = useState(true);
  const [currentScreenshots, setCurrentScreenshots] = useState<HelpScreenshotType[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const helpCategories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <HelpIcon />,
      topics: [
        'How to create a new document',
        'How to add categories',
        'How to add content',
        'Basic navigation',
      ]
    },
    {
      id: 'documents',
      title: 'Document Management',
      icon: <FileCopyIcon />,
      topics: [
        'Creating documents',
        'Opening documents',
        'Loading documents',
        'Closing documents',
        'Deleting documents',
        'Renaming documents',
        'Duplicating documents',
        'Merging documents',
        'Exporting documents',
      ]
    },
    {
      id: 'categories',
      title: 'Working with Categories',
      icon: <CategoryIcon />,
      topics: [
        'Adding categories',
        'Deleting categories',
        'Renaming categories',
        'Moving categories',
        'Copying and pasting categories',
        'Importing a subtree from another document',
        'Grouping categories',
        'Splitting categories',
        'Merging categories',
        'Flattening categories',
        'Category organization',
      ]
    },
    {
      id: 'content',
      title: 'Working with Content',
      icon: <DescriptionIcon />,
      topics: [
        'Adding content',
        'Editing content',
        'Deleting content',
        'Moving content',
        'Subcases',
        'Filtering the content pane by subcase',
        'Content organization',
      ]
    },
    {
      id: 'search',
      title: 'Search and Find',
      icon: <SearchIcon />,
      topics: [
        'Finding text in documents',
        'Semantic search',
        'Search options',
        'Navigation between matches',
      ]
    },
    {
      id: 'ai-features',
      title: 'AI Features',
      icon: <AutoAwesomeIcon />,
      topics: [
        'AI assistant',
        'Generating subtrees',
        'Suggesting trees from files',
        'Expanding documents with files',
        'AI prompts',
      ]
    },
    {
      id: 'shortcuts',
      title: 'Shortcuts & gestures',
      icon: <KeyboardIcon />,
      topics: [
        'Keyboard shortcuts',
        'Editing shortcuts',
        'Find shortcuts',
        'Navigation',
        'Mouse and gestures',
      ]
    },
    {
      id: 'settings',
      title: 'Settings and Preferences',
      icon: <SettingsIcon />,
      topics: [
        'Auto-paste settings',
        'Tooltip settings',
        'Help mode',
        'User preferences',
      ]
    }
  ];

  const quickQuestions = [
    'How do I create a new document?',
    'How do I load a document from a file?',
    'How do I add a category?',
    'How do I delete a category?',
    'How do I add content to a category?',
    'How do I filter the content pane by subcase?',
    'How do I search in my document?',
    'How do I delete a document?',
    'How do I rename a document?',
    'How do I use the AI assistant?',
    'How do I expand a document with files?',
    'How do I duplicate a category as a document?',
    'How do I import a subtree from another document?',
    'How do I merge categories?',
    'How do I export my document?',
  ];

  useEffect(() => {
    if (open) {
      // Add welcome message
      setMessages([{
        id: 'welcome',
        type: 'assistant',
        content: 'Hello! I\'m here to help you learn how to use Narralyze. You can ask me questions about any feature, or browse the help topics below. What would you like to know?',
        timestamp: new Date()
      }]);
    }
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getCategoryAnswer = (categoryId: string): string | null => {
    switch (categoryId) {
      case 'getting-started':
        return "Getting Started with Narralyze:\n\n1. Create a Document: Click the '+' button in the document panel to create your first document\n\n2. Add Categories: Select a category and click the '+' button to add subcategories for organization\n\n3. Add Content: Select a content category and click the edit icon to add your text content\n\n4. Use Subcases: Organize your content by creating subcases (like chapters or sections)\n\n5. Search: Use the search icon to find specific content in your document\n\n6. AI Features: Use the AI sparkles icon to get help with organization and content analysis";
      
      case 'categories':
        return "Working with Categories:\n\nAdding Categories:\n• Select a parent category and click the '+' button\n• Enter a name and description\n• Categories can contain subcategories OR content (not both)\n\nManaging Categories:\n• Rename: Toolbar rename icon, context menu, or select in the tree and click the same label again (inline rename)\n• Move: Use the up/down arrows to reorder categories\n• Copy/Paste: Toolbar icons or ⌘/Ctrl+C, ⌘/Ctrl+X, ⌘/Ctrl+V when focus is outside text fields\n• Import subtree from another document: Toolbar icon next to Paste, or tree context menu — pick a source doc and category subtree to clone under your selection\n• Group: Create a new parent category and move selected categories under it\n• Split: Divide content categories into two separate categories\n• Merge: Combine multiple selected categories into one (with conflict-handling options)\n• Flatten: Move all subcategories up one level and remove the parent";
      
      case 'content':
        return "Working with Content:\n\nAdding Content:\n• Select a content category (one without subcategories)\n• Click the edit icon in the toolbar\n• Enter your text and choose a subcase\n• Click 'Save' to store the content\n\nEditing Content:\n• Select a content item and click the edit icon\n• Modify the text and subcase as needed\n• Changes are saved automatically\n\nOrganizing Content:\n• Use subcases to organize content within categories\n• Create subcase groups for better organization\n• Use the content editor's rich text features\n• Content pane filter (toolbar filter icon): show only rows for chosen subcases — same controls as Show sub tree; hidden rows are not deleted\n\nMoving Content:\n• Select content items and use the move arrows\n• Drag and drop content between categories\n• Copy and paste content to duplicate it";
      
      case 'documents':
        return "Document Management:\n\nCreating Documents:\n• Click the '+' button in the document panel\n• Enter a name for your document\n• Start adding categories and content\n\nOpening Documents:\n• Click on a document in the document panel\n• Documents open in tabs for easy switching\n• You can work on multiple documents simultaneously\n\nLoading Documents:\n• Use the 'Load Document' feature in the document panel\n• Select a .nbr file created by the Narralyzer desktop application\n• The document will be imported and opened in a new tab\n• You can work on multiple documents simultaneously\n\nManaging Documents:\n• Rename: Right-click a document and select 'Rename'\n• Duplicate: Create a copy with selected subcases\n• Merge: Combine two documents into one\n• Export: Use the sub tree view to export as DOCX\n• Delete: Remove documents you no longer need";
      
      case 'search':
        return "Search and Find Features:\n\nText Search:\n• Click the search icon in the toolbar, or press ⌘/Ctrl+F when not typing in a regular input or textarea\n• Enter your search terms\n• Choose search options (case sensitive, whole word, etc.)\n• Use 'Find Next' or F3 / ⌘/Ctrl+G to navigate through matches (when Find has focus outside search fields)\n\nSearch Options:\n• Search in: Choose to search names, descriptions, or content\n• Search mode: Any (at least one term), All (all terms), or Exact phrase\n• Case sensitive: Match exact letter case\n• Whole word: Only match complete words\n\nSemantic Search:\n• Use the AI sparkles icon for natural language search\n• Ask questions in plain English\n• The AI understands meaning, not just exact text matches";
      
      case 'ai-features':
        return "AI Features in Narralyze:\n\nAI Assistant:\n• Click the AI sparkles icon to open the assistant\n• Ask questions about your document in natural language\n• Get help with organization and content analysis\n• The AI can suggest improvements and answer questions\n\nGenerating Subtrees:\n• Select a content category with text\n• Click the AI icon in the toolbar\n• The AI will analyze your content and suggest a hierarchical structure\n• Choose the maximum depth for the generated structure\n\nSuggesting Trees from Files:\n• Use the document panel to upload files (PDF, Word, etc.)\n• The AI will analyze the files and suggest a category structure\n• Provide context to help the AI generate better organization\n\nAI Prompts:\n• Be specific about what you want the AI to help with\n• Provide context about your document's purpose\n• Ask follow-up questions for more detailed help";
      
      case 'shortcuts':
        return (
          'Shortcuts & gestures — pick a topic on the left:\n\n' +
          '• Keyboard shortcuts — Undo/redo, cut, copy, paste, Delete/Backspace, move (↑/↓ or ⌘/Ctrl+↑↓) (outside text fields)\n' +
          '• Editing shortcuts — Inline rename keys, semantic search field, editor note\n' +
          '• Find shortcuts — Open Find, matches, jump between categories\n' +
          '• Navigation — Tabs, tree focus, Tab key\n' +
          '• Mouse and gestures — Multi-select, drag-and-drop, context menus'
        );
      
      case 'settings':
        return "Settings and Preferences:\n\nAuto-Paste:\n• Enable to automatically paste clipboard content when adding new content\n• Saves time when working with external text sources\n• Toggle in the settings menu\n\nTooltip Settings:\n• Help Mode: Enable detailed help tooltips\n• Short Tooltips: Show brief tooltips for all UI elements\n• Font Size: Adjust tooltip text size for better readability\n\nUser Preferences:\n• Change your avatar in the user menu\n• Upload custom avatars or choose from predefined ones\n• Settings are saved automatically\n\nHelp Mode:\n• Enable comprehensive help information\n• Tooltips show detailed explanations when enabled\n• Great for learning the application";
      
      default:
        return null;
    }
  };

  const handleSendQuestion = async () => {
    if (!question.trim()) return;

    const trimmedQuestion = question.trim();
    const userMessage: HelpMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: trimmedQuestion,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setQuestion('');
    setIsLoading(true);

    try {
      // If user typed an exact sidebar/quick topic, prefer the deterministic fallback answer
      // (avoids surprising backend answers for canned topics).
      const isKnownTopic = helpCategories.some((c) => c.topics.includes(trimmedQuestion)) || quickQuestions.includes(trimmedQuestion);
      if (isKnownTopic) {
        const answer = getFallbackAnswer(trimmedQuestion);
        const assistantMessage: HelpMessage = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: answer,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);

        const screenshots = getScreenshotsForTopic(trimmedQuestion);
        setCurrentScreenshots(screenshots);
        setIsLoading(false);
        return;
      }

      const endpoint = useSemanticSearch ? '/help/semantic-search' : '/help/ask';
      const response = await api.post(endpoint, {
        question: trimmedQuestion,
        context: selectedCategory ? selectedCategory : (useSemanticSearch ? 'semantic' : 'pattern')
      });

      // If we have a selected category and the API response seems generic,
      // try to get a more specific answer from our fallback system
      let answer = response.data.answer;
      if (selectedCategory && (answer.includes('Adding Categories') || answer.includes('Managing Categories'))) {
        // The API returned generic category content, try to get a specific answer
        const specificAnswer = getFallbackAnswer(trimmedQuestion);
        if (specificAnswer && !specificAnswer.includes('I understand you\'re asking about')) {
          answer = specificAnswer;
        }
      }

      const assistantMessage: HelpMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: answer,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
      
      // Get screenshots for this topic
      // IMPORTANT: use the original user question, not the cleared state variable.
      const screenshots = getScreenshotsForTopic(trimmedQuestion);
      setCurrentScreenshots(screenshots);
      
      // If no screenshots found, try to get them based on the answer content
      if (screenshots.length === 0) {
        const answerScreenshots = getScreenshotsForAnswer(answer);
        setCurrentScreenshots(answerScreenshots);
      }
    } catch (error) {
      console.error('Error asking help question:', error);
      let fallbackAnswer = getFallbackAnswer(question);
      const errorMessage: HelpMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: fallbackAnswer,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);

      // Still attempt to show screenshots for the user's question on error.
      const screenshots = getScreenshotsForTopic(trimmedQuestion);
      setCurrentScreenshots(screenshots);
    } finally {
      setIsLoading(false);
    }
  };

  const getFallbackAnswer = (question: string): string => {
    const trimmedLower = question.trim().toLowerCase();
    const shortcutsTopicAnswer = getShortcutsSubtopicAnswer(trimmedLower);
    if (shortcutsTopicAnswer) {
      return shortcutsTopicAnswer;
    }

    const lowerQuestion = trimmedLower;

    // Document management
    if (lowerQuestion.includes('create') && lowerQuestion.includes('document')) {
      return "To create a new document:\n1. Click the '+' button in the document panel\n2. Enter a name for your document\n3. Click 'Create' to start working";
    }
    
    if (lowerQuestion.includes('creating') && lowerQuestion.includes('document')) {
      return "To create a new document:\n1. Click the '+' button in the document panel\n2. Enter a name for your document\n3. Click 'Create' to start working";
    }
    
    if (lowerQuestion.includes('open') && lowerQuestion.includes('document')) {
      return "To open a document:\n1. Click on any document in the document panel\n2. The document will open in a new tab\n3. You can work on multiple documents simultaneously";
    }
    
    if (lowerQuestion.includes('opening') && lowerQuestion.includes('document')) {
      return "To open a document:\n1. Click on any document in the document panel\n2. The document will open in a new tab\n3. You can work on multiple documents simultaneously";
    }
    
    if (lowerQuestion.includes('load') && lowerQuestion.includes('document')) {
      return "To load a document from a .nbr file:\n1. Use the 'Load Document' feature in the document panel\n2. Select a .nbr file created by the Narralyzer desktop application\n3. The document will be imported and opened in a new tab\n4. You can work on multiple documents simultaneously";
    }
    
    if (lowerQuestion.includes('load') && lowerQuestion.includes('file')) {
      return "To load a document from a .nbr file:\n1. Use the 'Load Document' feature in the document panel\n2. Select a .nbr file created by the Narralyzer desktop application\n3. The document will be imported and opened in a new tab\n4. You can work on multiple documents simultaneously";
    }
    
    if (lowerQuestion.includes('nbr') && lowerQuestion.includes('file')) {
      return "To load a document from a .nbr file:\n1. Use the 'Load Document' feature in the document panel\n2. Select a .nbr file created by the Narralyzer desktop application\n3. The document will be imported and opened in a new tab\n4. You can work on multiple documents simultaneously";
    }
    
    if (lowerQuestion.includes('close') && lowerQuestion.includes('document')) {
      return "To close a document:\n1. Click the 'X' button on the document tab\n2. Or right-click the document tab and select 'Close'\n3. The document will be saved and removed from active tabs";
    }
    
    if (lowerQuestion.includes('closing') && lowerQuestion.includes('document')) {
      return "To close a document:\n1. Click the 'X' button on the document tab\n2. Or right-click the document tab and select 'Close'\n3. The document will be saved and removed from active tabs";
    }
    
    if (lowerQuestion.includes('rename') && lowerQuestion.includes('document')) {
      return "To rename a document:\n1. Right-click on the document in the document panel\n2. Select 'Rename' from the context menu\n3. Enter the new name and press Enter";
    }
    
    if (lowerQuestion.includes('renaming') && lowerQuestion.includes('document')) {
      return "To rename a document:\n1. Right-click on the document in the document panel\n2. Select 'Rename' from the context menu\n3. Enter the new name and press Enter";
    }
    
    if (lowerQuestion.includes('delete') && lowerQuestion.includes('document')) {
      return "To delete a document:\n1. Right-click on the document in the document panel\n2. Select 'Delete' from the context menu\n3. Confirm the deletion in the dialog";
    }
    
    if (lowerQuestion.includes('deleting') && lowerQuestion.includes('document')) {
      return "To delete a document:\n1. Right-click on the document in the document panel\n2. Select 'Delete' from the context menu\n3. Confirm the deletion in the dialog";
    }
    
    if (lowerQuestion.includes('duplicate') && lowerQuestion.includes('document')) {
      return "To duplicate a document:\n1. Right-click on the document in the document panel\n2. Select 'Duplicate' from the context menu\n3. Choose which subcases to include\n4. Enter a name for the new document";
    }
    
    if (lowerQuestion.includes('duplicating') && lowerQuestion.includes('document')) {
      return "To duplicate a document:\n1. Right-click on the document in the document panel\n2. Select 'Duplicate' from the context menu\n3. Choose which subcases to include\n4. Enter a name for the new document";
    }
    
    if (lowerQuestion.includes('merge') && lowerQuestion.includes('document')) {
      return "To merge documents:\n1. Open the target document\n2. Click 'Merge Document' in the document menu\n3. Select the source document to merge from\n4. Choose how to handle conflicts\n5. Click 'Merge' to combine the documents";
    }
    
    if (lowerQuestion.includes('merging') && lowerQuestion.includes('document')) {
      return "To merge documents:\n1. Open the target document\n2. Click 'Merge Document' in the document menu\n3. Select the source document to merge from\n4. Choose how to handle conflicts\n5. Click 'Merge' to combine the documents";
    }
    
    if (lowerQuestion.includes('export') && lowerQuestion.includes('document')) {
      return "To export your document:\n1. Use the 'Show Sub Tree' feature to view your document structure\n2. Click the 'Print' button to print the view\n3. Click the 'Export DOCX' button to download as a Word document";
    }
    
    if (lowerQuestion.includes('exporting') && lowerQuestion.includes('document')) {
      return "To export your document:\n1. Use the 'Show Sub Tree' feature to view your document structure\n2. Click the 'Print' button to print the view\n3. Click the 'Export DOCX' button to download as a Word document";
    }
    
    // Category management
    if (lowerQuestion.includes('add') && (lowerQuestion.includes('category') || lowerQuestion.includes('categories'))) {
      return "To add a category:\n1. Select a parent category (or root for top-level)\n2. Click the '+' button in the toolbar\n3. Enter a name and description\n4. Click 'Add' to create the category";
    }
    
    if ((lowerQuestion.includes('rename') || lowerQuestion.includes('renaming')) && (lowerQuestion.includes('category') || lowerQuestion.includes('categories'))) {
      return "To rename a category:\n1. Select the category you want to rename\n2. Click the rename icon (pencil) in the toolbar\n3. Enter the new name and press Enter\n4. Or right-click and select 'Rename' from the context menu";
    }
    
    if (lowerQuestion.includes('delete') && (lowerQuestion.includes('category') || lowerQuestion.includes('categories'))) {
      return "To delete a category:\n1. Select the category you want to delete\n2. Click the delete icon (trash) in the toolbar, or press Delete or Backspace when focus is not in a text field or the rich-text editor\n3. Confirm the deletion in the dialog\n4. Or right-click and select 'Delete' from the context menu";
    }
    
    if (lowerQuestion.includes('move') && (lowerQuestion.includes('category') || lowerQuestion.includes('categories'))) {
      return "To move a category:\n1. Select the category you want to move\n2. Use the up/down arrow buttons in the toolbar, or ↑ / ↓ when focus is outside the category tree, or ⌘/Ctrl+↑ / ⌘/Ctrl+↓ (also when the tree is focused)\n3. Or drag and drop the category to a new position\n4. Categories can be moved within the same parent";
    }
    
    if (lowerQuestion.includes('moving') && (lowerQuestion.includes('category') || lowerQuestion.includes('categories'))) {
      return "To move a category:\n1. Select the category you want to move\n2. Use the up/down arrow buttons in the toolbar, or ↑ / ↓ when focus is outside the category tree, or ⌘/Ctrl+↑ / ⌘/Ctrl+↓ (also when the tree is focused)\n3. Or drag and drop the category to a new position\n4. Categories can be moved within the same parent";
    }
    
    if (lowerQuestion.includes('duplicate') && (lowerQuestion.includes('category') || lowerQuestion.includes('categories'))) {
      return "To duplicate a category:\n1. Select the category you want to duplicate\n2. Click the duplicate icon in the toolbar\n3. Choose which subcategories to include\n4. Enter a name for the new category\n5. Click 'Duplicate' to create the copy";
    }
    
    if (lowerQuestion.includes('merge') && (lowerQuestion.includes('category') || lowerQuestion.includes('categories'))) {
      return "To merge categories:\n1. Select multiple categories (⌘/Ctrl+click or Shift+click)\n2. Click the merge icon in the toolbar\n3. Enter a name for the merged category\n4. Click 'Merge' to combine the categories";
    }
    
    if (lowerQuestion.includes('merging') && (lowerQuestion.includes('category') || lowerQuestion.includes('categories'))) {
      return "To merge categories:\n1. Select multiple categories (⌘/Ctrl+click or Shift+click)\n2. Click the merge icon in the toolbar\n3. Enter a name for the merged category\n4. Click 'Merge' to combine the categories";
    }
    
    if (lowerQuestion.includes('split') && (lowerQuestion.includes('category') || lowerQuestion.includes('categories'))) {
      return "To split a category:\n1. Select the category you want to split\n2. Click the split icon in the toolbar\n3. Choose how to divide the content\n4. Enter names for the new categories\n5. Click 'Split' to create separate categories";
    }
    
    if (lowerQuestion.includes('flatten') && (lowerQuestion.includes('category') || lowerQuestion.includes('categories'))) {
      return "To flatten a category:\n1. Select the category you want to flatten\n2. Click the flatten icon in the toolbar\n3. This will move all subcategories up one level\n4. The parent category will be removed";
    }
    
    if (lowerQuestion.includes('join') && (lowerQuestion.includes('category') || lowerQuestion.includes('categories'))) {
      return "The \"Join categories\" operation was replaced by \"Group categories\".\n\nTo group categories:\n1. Select multiple categories (⌘/Ctrl+click or Shift+click)\n2. Click \"Group categories\"\n3. Enter a name for the new parent category\n4. The selected categories will become children of the new parent";
    }
    
    // Content management
    // Content pane subcase filter (before vague "subcase" answer below)
    if (
      trimmedLower === 'filtering the content pane by subcase' ||
      trimmedLower === 'how do i filter the content pane by subcase?' ||
      (lowerQuestion.includes('content pane') &&
        (lowerQuestion.includes('filter') || lowerQuestion.includes('filtering')) &&
        lowerQuestion.includes('subcase'))
    ) {
      return (
        'Filtering the content pane by subcase\n\n' +
        'Limit which subcases appear as rows in the content table for the selected category. Other subcases are hidden until you clear or change the filter — nothing is deleted.\n\n' +
        '1. Open a document that has subcases.\n' +
        '2. Click the filter icon (filter list) in the toolbar near Find.\n' +
        '3. Use the same pattern as Show sub tree: subcase group chips, Select all, and individual checkboxes. The content table updates while the dialog stays open.\n' +
        '4. Choosing every document subcase clears the filter (show all rows again). Cancel restores the filter from when you opened the dialog.\n\n' +
        'While a filter is on, a badge shows how many subcases are included and the icon pulses slightly (unless reduced motion is enabled). This is separate from the subcase dropdown in the content panel header, which chooses which subcase you are editing.'
      );
    }

    if (lowerQuestion.includes('add') && lowerQuestion.includes('content')) {
      return "To add content to a category:\n1. Select a content category (one without subcategories)\n2. Click the edit icon in the toolbar\n3. Enter your text content\n4. Choose a subcase for organization\n5. Click 'Save' to store the content";
    }
    
    if (lowerQuestion.includes('edit') && lowerQuestion.includes('content')) {
      return "To edit content:\n1. Select the content item you want to edit\n2. Click the edit icon in the toolbar\n3. Modify the text and subcase as needed\n4. Changes are saved automatically";
    }
    
    if (lowerQuestion.includes('delete') && lowerQuestion.includes('content')) {
      return "To delete content:\n1. Select the content item you want to delete\n2. Click the delete icon in the toolbar, or press Delete or Backspace when focus is not in a text field or the rich-text editor\n3. Confirm the deletion in the dialog";
    }
    
    if (lowerQuestion.includes('move') && lowerQuestion.includes('content')) {
      return "To move content:\n1. Select the content item you want to move\n2. Use the up/down arrow buttons in the toolbar, or ↑ / ↓ when focus is outside the category tree, or ⌘/Ctrl+↑ / ⌘/Ctrl+↓ (also when the tree is focused)\n3. Or drag and drop the content to a new category";
    }
    
    if (lowerQuestion.includes('moving') && lowerQuestion.includes('content')) {
      return "To move content:\n1. Select the content item you want to move\n2. Use the up/down arrow buttons in the toolbar, or ↑ / ↓ when focus is outside the category tree, or ⌘/Ctrl+↑ / ⌘/Ctrl+↓ (also when the tree is focused)\n3. Or drag and drop the content to a new category";
    }
    
    if (lowerQuestion.includes('delete') && lowerQuestion.includes('content')) {
      return "To delete content:\n1. Select the content item you want to delete\n2. Click the delete icon in the toolbar, or press Delete or Backspace when focus is not in a text field or the rich-text editor\n3. Confirm the deletion in the dialog";
    }
    
    if (lowerQuestion.includes('deleting') && lowerQuestion.includes('content')) {
      return "To delete content:\n1. Select the content item you want to delete\n2. Click the delete icon in the toolbar, or press Delete or Backspace when focus is not in a text field or the rich-text editor\n3. Confirm the deletion in the dialog";
    }
    

    
    if (lowerQuestion.includes('content organization') || question === 'Content organization') {
      return "Content Organization Tips:\n\nUsing Subcases:\n• Create subcases to organize content within categories\n• Use subcase groups for better organization\n• Assign content to specific subcases for better structure\n\nMoving Content:\n• Drag and drop content between categories\n• Use the move arrows to reorder content\n• Copy and paste content to duplicate it\n\nContent Categories:\n• Categories can contain either subcategories OR content (not both)\n• Use content categories for storing text\n• Use regular categories for organization";
    }
    
    if (lowerQuestion.includes('category organization') || question === 'Category organization') {
      return "Category Organization Tips:\n\nHierarchical Structure:\n• Create a logical hierarchy with main categories and subcategories\n• Use descriptive names that clearly indicate the category's purpose\n• Keep the structure consistent throughout your document\n\nOrganizing Principles:\n• Group related items together under common parent categories\n• Use categories for organization, content categories for actual text\n• Maintain a balance between depth and breadth in your structure\n\nBest Practices:\n• Start with broad categories and add specific subcategories as needed\n• Use consistent naming conventions across your document\n• Regularly review and reorganize categories as your content grows\n• Consider using the AI assistant to suggest optimal category structures";
    }
    
    if (lowerQuestion.includes('generate') && lowerQuestion.includes('subtree')) {
      return "To generate a subtree:\n1. Select a content category with text\n2. Click the AI sparkles icon in the toolbar\n3. Choose the maximum depth for the generated structure\n4. The AI will analyze your content and suggest a hierarchical structure\n5. Review and accept the suggested organization";
    }
    
    if (lowerQuestion.includes('generating') && lowerQuestion.includes('subtree')) {
      return "To generate a subtree:\n1. Select a content category with text\n2. Click the AI sparkles icon in the toolbar\n3. Choose the maximum depth for the generated structure\n4. The AI will analyze your content and suggest a hierarchical structure\n5. Review and accept the suggested organization";
    }
    

    
    if (lowerQuestion.includes('help mode') || lowerQuestion === 'help mode' || question === 'Help mode') {
      return "Help Mode:\n\nEnabling Help Mode:\n• Go to Settings in the user menu\n• Enable 'Help Mode' in the settings\n• This will show detailed tooltips for all UI elements\n\nUsing Help Mode:\n• Hover over any button or feature to see detailed explanations\n• Tooltips will show step-by-step instructions\n• Great for learning the application features\n\nGetting Help:\n• Click the help icon (?) in the toolbar\n• Browse help topics in the left sidebar\n• Ask specific questions in the chat interface";
    }
    
    if (lowerQuestion.includes('copy') && lowerQuestion.includes('content')) {
      return "To copy content:\n1. Select the content item you want to copy\n2. Click the copy icon in the toolbar\n3. Select the destination category\n4. Click the paste icon to create a copy";
    }
    
    // Copy/Cut/Paste operations
    if (lowerQuestion.includes('copy') && !lowerQuestion.includes('content')) {
      return "To copy and paste categories/items:\n1. Select the item(s) you want to copy\n2. Click the copy icon (or use ⌘/Ctrl+C when focus is outside text fields — same for cut ⌘/Ctrl+X and paste ⌘/Ctrl+V)\n3. Select the destination category/location\n4. Click the paste icon (or ⌘/Ctrl+V) to insert a copy";
    }
    
    if (lowerQuestion.includes('cut')) {
      return "To cut items:\n1. Select the item(s) you want to cut\n2. Click the cut icon in the toolbar\n3. Or use ⌘/Ctrl+X (when focus is outside text fields and the rich-text editor)\n4. The item is placed on the clipboard for pasting";
    }
    
    if (lowerQuestion.includes('paste')) {
      return "To paste items:\n1. Select the destination category\n2. Click the paste icon in the toolbar\n3. Or use ⌘/Ctrl+V (when focus is outside text fields and the rich-text editor)\n4. The clipboard item is pasted";
    }
    
    // Undo/Redo
    if (lowerQuestion.includes('undo')) {
      return "To undo your last action:\n1. Click the undo icon in the toolbar\n2. Or use ⌘/Ctrl+Z (when focus is outside text fields and the rich-text editor)\n3. You can undo multiple actions in sequence";
    }
    
    if (lowerQuestion.includes('redo')) {
      return "To redo an action:\n1. Click the redo icon in the toolbar\n2. Or use ⌘/Ctrl+Shift+Z (when focus is outside text fields and the rich-text editor)\n3. This restores actions you previously undid";
    }
    
    // Subcases
    if (lowerQuestion.includes('subcase')) {
      return "Subcases help organize content within categories:\n1. When adding content, choose a subcase from the dropdown\n2. Create new subcases by typing a new name\n3. Use subcase groups to organize related subcases\n4. Subcases appear as orange sticky notes in the tree view";
    }
    
    // Search functionality
    if (lowerQuestion.includes('search')) {
      return "To search in your document:\n1. Click the search icon in the toolbar\n2. Enter your search terms\n3. Choose search options (case sensitive, whole word, etc.)\n4. Use 'Find Next' to navigate through matches\n5. Search can look in names, descriptions, or content";
    }
    
    if (lowerQuestion.includes('find')) {
      return "To find content in your document:\n1. Click the search icon in the toolbar\n2. Enter your search terms\n3. Choose where to search (names, descriptions, content)\n4. Use 'Find Next' and 'Find Previous' to navigate\n5. Results are highlighted in the tree view";
    }
    
    // AI features
    if (lowerQuestion.includes('ai') || lowerQuestion.includes('assistant')) {
      return "To use the AI assistant:\n1. Click the AI sparkles icon in the toolbar\n2. Ask your question in natural language\n3. The AI will analyze your document and provide helpful answers\n4. You can ask follow-up questions in the conversation";
    }
    
    if (lowerQuestion.includes('generate') && lowerQuestion.includes('subtree')) {
      return "To generate a subtree:\n1. Select a content category with text\n2. Click the AI icon in the toolbar\n3. Choose the maximum depth for the structure\n4. The AI will analyze your content and suggest organization\n5. Review and accept the suggested structure";
    }
    
    if (lowerQuestion.includes('suggest') && lowerQuestion.includes('tree')) {
      return "To suggest a tree from files:\n1. Click 'Suggest Tree from Files' in the document panel\n2. Select one or more files (PDF, Word, etc.)\n3. Provide context to help the AI\n4. Choose maximum depth and other options\n5. The AI will analyze files and suggest a category structure";
    }
    
    // Navigation
    if (lowerQuestion.includes('navigate') || lowerQuestion.includes('navigation')) {
      return "Navigation in Narralyze:\n• Click on categories to select them\n• Use the tree view to expand/collapse categories\n• Use tabs to switch between documents\n• Use the search feature to find specific content\n• Use keyboard shortcuts for faster navigation";
    }
    
    // Settings
    if (lowerQuestion.includes('settings') || lowerQuestion.includes('preferences')) {
      return "To access settings:\n1. Click the settings icon in the toolbar\n2. Configure auto-paste from clipboard\n3. Adjust tooltip font size\n4. Enable detailed help tooltips\n5. Settings are saved automatically";
    }
    
    if (lowerQuestion.includes('auto') && lowerQuestion.includes('paste')) {
      return "Auto-paste feature:\n1. Enable in settings to automatically paste clipboard content\n2. Saves time when adding new content\n3. Works when you click the edit icon\n4. Toggle on/off in the settings menu";
    }
    
    // Shortcuts & gestures — vague chat queries (exact sidebar titles handled by getShortcutsSubtopicAnswer)
    if (
      lowerQuestion.includes('shortcut') ||
      lowerQuestion.includes('keyboard') ||
      (lowerQuestion.includes('mouse') && lowerQuestion.includes('gesture'))
    ) {
      return (
        'Shortcuts & gestures are grouped by topic in the left sidebar under Shortcuts & gestures:\n\n' +
        '• Keyboard shortcuts — Undo/redo, cut, copy, paste, Delete/Backspace, move (↑/↓ or ⌘/Ctrl+↑↓) (outside text fields)\n' +
        '• Editing shortcuts — Inline rename, semantic AI field, rich-text editor note\n' +
        '• Find shortcuts — Open Find, match navigation, jump between categories\n' +
        '• Navigation — Documents, tree, Tab, search\n' +
        '• Mouse and gestures — Multi-select, double-click, rename click, drag-and-drop\n\n' +
        'Open one of those topics for the full key list.'
      );
    }
    
    // Show sub tree
    if (lowerQuestion.includes('show') && lowerQuestion.includes('subtree')) {
      return "To show a sub tree:\n1. Select a category you want to focus on\n2. Click the 'Show Sub Tree' button\n3. Choose which subcases to include\n4. This creates a focused view of that category\n5. Use Print or Export DOCX to save the view";
    }
    
    // Duplicate as document
    if (lowerQuestion.includes('duplicate') && lowerQuestion.includes('document')) {
      return "To duplicate a category as a document:\n1. Select the category you want to duplicate\n2. Click the duplicate icon in the toolbar\n3. Choose which subcategories to include\n4. Enter a name for the new document\n5. Click 'Duplicate' to create the new document";
    }
    
    // Print functionality
    if (lowerQuestion.includes('print')) {
      return "To print your document view:\n1. Use the 'Show Sub Tree' feature to view your document structure\n2. Click the 'Print' button in the sub tree view\n3. Choose your print settings\n4. The view will be printed as formatted";
    }
    
    // File upload
    if (lowerQuestion.includes('upload') || lowerQuestion.includes('file')) {
      return "To upload files for AI analysis:\n1. Click 'Suggest Tree from Files' in the document panel\n2. Select PDF, Word, or other text files\n3. Provide context to help the AI\n4. Choose maximum depth and other options\n5. The AI will analyze files and suggest a category structure";
    }
    
    // Import subtree from another document (before generic "import" → AI files)
    if (
      trimmedLower === 'importing a subtree from another document' ||
      trimmedLower === 'how do i import a subtree from another document?' ||
      (lowerQuestion.includes('import') &&
        lowerQuestion.includes('subtree') &&
        lowerQuestion.includes('document') &&
        !lowerQuestion.includes('file') &&
        !lowerQuestion.includes('nbr') &&
        !lowerQuestion.includes('suggest'))
    ) {
      return (
        'Importing a subtree from another document\n\n' +
        'Clone a category branch from any of your other documents into the category you have selected in this document (new categories and content — same idea as copy/paste across tabs).\n\n' +
        '1. Select the destination branch category (same placement rules as Paste: it must accept new subcategories; clear multi-select in the tree and content list).\n' +
        '2. Open the action:\n' +
        '   • Toolbar — Import (folder-with-upload icon) next to Paste\n' +
        '   • Or right-click the category in the tree → Import subtree from document…\n' +
        '3. Choose a source document (the current document is not listed).\n' +
        '4. Pick the category whose subtree you want; confirm Import.\n\n' +
        'Notes:\n' +
        '• Cut/paste moves categories only within one document.\n' +
        '• Copy then switch tabs and Paste still duplicates across documents without using this dialog.'
      );
    }

    // Import functionality (files / AI)
    if (lowerQuestion.includes('import')) {
      return "To import files for AI analysis:\n1. Click 'Suggest Tree from Files' in the document panel\n2. Select PDF, Word, or other text files\n3. Provide context to help the AI\n4. Choose maximum depth and other options\n5. The AI will analyze files and suggest a category structure";
    }
    
    // Advanced search
    if (lowerQuestion.includes('whole word') || lowerQuestion.includes('case sensitive')) {
      return "Advanced search options:\n• Whole word: Only match complete words\n• Case sensitive: Match exact letter case\n• Search in: Choose names, descriptions, or content\n• Search mode: Any, All, or Exact phrase\n• Use these options in the search dialog for more precise results";
    }
    
    // Subcase groups
    if (lowerQuestion.includes('subcase group')) {
      return "To organize subcases with groups:\n1. When adding content, use the subcase groups feature\n2. Create groups to organize related subcases\n3. Assign subcases to groups for better structure\n4. Groups help maintain organization as content grows\n5. Use the subcase group dialog to manage your organization";
    }
    
    // Basic operations
    if (lowerQuestion.includes('basic') || lowerQuestion.includes('getting started')) {
      return "Getting Started with Narralyze:\n\n1. Create a Document: Click the '+' button in the document panel\n\n2. Add Categories: Select a category and click the '+' button to add subcategories\n\n3. Add Content: Select a content category and click the edit icon to add text\n\n4. Use Subcases: Organize content by creating subcases\n\n5. Search: Use the search icon to find specific content\n\n6. AI Features: Use the AI sparkles icon for help with organization";
    }
    
    // Default answer
    return "I understand you're asking about \"" + question + "\". Here are some general tips:\n\n" +
           "• Use the toolbar buttons to perform actions on selected categories or content\n" +
           "• Right-click on items for context menus with additional options\n" +
           "• Use the search feature to find specific content\n" +
           "• Try the AI assistant for help with organization and analysis\n" +
           "• Check the help topics in the left sidebar for detailed guides\n\n" +
           "Could you be more specific about what you'd like to do? I can provide more targeted help.";
  };

  const handleQuickQuestion = (q: string) => {
    setQuestion(q);
    // Automatically send the question when clicked from the sidebar
    const userMessage: HelpMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: q,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // Use the fallback answer system for quick questions
    const answer = getFallbackAnswer(q);
    const assistantMessage: HelpMessage = {
      id: (Date.now() + 1).toString(),
      type: 'assistant',
      content: answer,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, assistantMessage]);
    
    // Get screenshots for this topic
    const screenshots = getScreenshotsForTopic(q);
    setCurrentScreenshots(screenshots);
    
    setIsLoading(false);
  };

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  const getHelpContent = (key: HelpContentKey) => {
    return helpContent[key] || { simple: key, detailed: 'Help content not found.' };
  };

  const getScreenshotsForTopic = (topic: string) => {
    return HelpScreenshotService.getScreenshotsForTopic(topic);
  };

  const getScreenshotsForAnswer = (answer: string) => {
    const lowerAnswer = answer.toLowerCase();
    
    // Check if the answer contains load document related content
    if (lowerAnswer.includes('load') && (lowerAnswer.includes('document') || lowerAnswer.includes('file'))) {
      return HelpScreenshotService.getScreenshotsForTopic('load document');
    }
    
    // Check if the answer contains create document related content
    if (
      lowerAnswer.includes('create a new document') ||
      lowerAnswer.includes('create document') ||
      lowerAnswer.includes('new document')
    ) {
      return HelpScreenshotService.getScreenshotsForTopic('create document');
    }

    // Check if the answer contains open document related content
    if (lowerAnswer.includes('open') && lowerAnswer.includes('document')) {
      return HelpScreenshotService.getScreenshotsForTopic('open document');
    }

    // Check if the answer contains close document related content
    if (lowerAnswer.includes('close') && lowerAnswer.includes('document')) {
      return HelpScreenshotService.getScreenshotsForTopic('close document');
    }

    // Check if the answer contains delete document related content
    if (lowerAnswer.includes('delete') && lowerAnswer.includes('document')) {
      return HelpScreenshotService.getScreenshotsForTopic('delete document');
    }

    // Check if the answer contains rename document related content
    if (lowerAnswer.includes('rename') && lowerAnswer.includes('document')) {
      return HelpScreenshotService.getScreenshotsForTopic('rename document');
    }
    
    // Check if the answer contains duplicate document related content
    if (lowerAnswer.includes('duplicate') && lowerAnswer.includes('document')) {
      return HelpScreenshotService.getScreenshotsForTopic('duplicate document');
    }

    // Check if the answer contains suggest tree from files related content
    if (
      (lowerAnswer.includes('suggest') && lowerAnswer.includes('tree')) ||
      (lowerAnswer.includes('suggest') && lowerAnswer.includes('files')) ||
      (lowerAnswer.includes('category') && lowerAnswer.includes('tree') && lowerAnswer.includes('files'))
    ) {
      return HelpScreenshotService.getScreenshotsForTopic('suggest tree from files');
    }

    // Check if the answer contains expand document with files related content
    if (
      (lowerAnswer.includes('expand') && lowerAnswer.includes('document')) ||
      (lowerAnswer.includes('expand') && lowerAnswer.includes('files')) ||
      (lowerAnswer.includes('expand') && lowerAnswer.includes('with files'))
    ) {
      return HelpScreenshotService.getScreenshotsForTopic('expand document with files');
    }

    // Check if the answer contains merge document related content
    if (lowerAnswer.includes('merge') && lowerAnswer.includes('document')) {
      return HelpScreenshotService.getScreenshotsForTopic('merge document');
    }

    // Check if the answer contains split category related content
    if (lowerAnswer.includes('split') && (lowerAnswer.includes('category') || lowerAnswer.includes('categories'))) {
      return HelpScreenshotService.getScreenshotsForTopic('split category');
    }

    // Check if the answer contains add content related content
    if (
      lowerAnswer.includes('add content') ||
      lowerAnswer.includes('adding content') ||
      lowerAnswer.includes('add a content')
    ) {
      return HelpScreenshotService.getScreenshotsForTopic('add content');
    }

    // Check if the answer contains rename category related content
    if (lowerAnswer.includes('rename') && lowerAnswer.includes('category')) {
      return HelpScreenshotService.getScreenshotsForTopic('rename category');
    }

    // Check if the answer contains delete category related content
    if (lowerAnswer.includes('delete') && lowerAnswer.includes('category')) {
      return HelpScreenshotService.getScreenshotsForTopic('delete category');
    }

    // Check if the answer contains group categories related content
    if (lowerAnswer.includes('group') && lowerAnswer.includes('categor')) {
      return HelpScreenshotService.getScreenshotsForTopic('group categories');
    }

    // Check if the answer contains move category related content
    if (lowerAnswer.includes('mov') && lowerAnswer.includes('categor')) {
      return HelpScreenshotService.getScreenshotsForTopic('moving categories');
    }

    // Check if the answer contains copy/paste category related content
    if ((lowerAnswer.includes('copy') || lowerAnswer.includes('paste')) && lowerAnswer.includes('categor')) {
      return HelpScreenshotService.getScreenshotsForTopic('copying and pasting categories');
    }

    // Check if the answer contains merge categories related content
    if (lowerAnswer.includes('merge') && lowerAnswer.includes('categor')) {
      return HelpScreenshotService.getScreenshotsForTopic('merging categories');
    }

    // Check if the answer contains flatten category related content
    if (lowerAnswer.includes('flatten') && lowerAnswer.includes('categor')) {
      return HelpScreenshotService.getScreenshotsForTopic('flattening categories');
    }
    
    return [];
  };

  const renderMessage = (message: HelpMessage) => (
    <Box
      key={message.id}
      sx={{
        display: 'flex',
        justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
        mb: 2,
      }}
    >
      <Paper
        sx={{
          p: 2,
          maxWidth: '80%',
          backgroundColor: message.type === 'user' ? 'primary.main' : 'grey.100',
          color: message.type === 'user' ? 'white' : 'text.primary',
        }}
      >
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
          {message.content}
        </Typography>
      </Paper>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpIcon />
          <Typography variant="h6">Help & Support</Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: 'grey.500' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', height: '100%' }}>
          {/* Left sidebar with help categories */}
          <Box sx={{ width: 300, borderRight: 1, borderColor: 'divider', p: 2, overflowY: 'auto' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Help Topics</Typography>
            
            {/* Quick Questions */}
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
              Quick Questions
            </Typography>
            <Box sx={{ mb: 3 }}>
              {quickQuestions.map((q, index) => (
                <Chip
                  key={index}
                  label={q}
                  size="small"
                  onClick={() => handleQuickQuestion(q)}
                  sx={{ m: 0.5, cursor: 'pointer' }}
                  variant="outlined"
                />
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Help Categories */}
            {helpCategories.map((category) => (
              <Accordion
                key={category.id}
                expanded={selectedCategory === category.id}
                onChange={() => handleCategorySelect(selectedCategory === category.id ? '' : category.id)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {category.icon}
                    <Typography variant="subtitle2">{category.title}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {category.topics.map((topic, index) => (
                      <ListItem
                        key={index}
                        button
                        onClick={() => handleQuickQuestion(topic)}
                        sx={{ py: 0.5 }}
                      >
                        <ListItemText
                          primary={topic}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {/* Right side with chat */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Messages area */}
            <Box sx={{ flex: 1, p: 2, overflowY: 'auto' }}>
              {messages.map(renderMessage)}
              
              {/* Screenshots */}
              {showScreenshots && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Visual Guide
                  </Typography>
                  {currentScreenshots.length > 0 ? (
                    currentScreenshots.map((screenshot) => (
                      <HelpScreenshot key={screenshot.id} screenshot={screenshot} />
                    ))
                  ) : (
                    <Box sx={{ p: 2, backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        No screenshots available for this topic yet.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              

              

              
              {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                  <Paper sx={{ p: 2, backgroundColor: 'grey.100' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2">Thinking...</Typography>
                    </Box>
                  </Paper>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </Box>

            {/* Input area */}
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
              {/* Toggles */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useSemanticSearch}
                      onChange={(e) => setUseSemanticSearch(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AutoAwesomeIcon fontSize="small" />
                      Semantic Search
                    </Typography>
                  }
                />
                <Tooltip title="Semantic search uses AI to understand the meaning of your question, not just exact word matches">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={showScreenshots}
                      onChange={(e) => setShowScreenshots(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <VisibilityIcon fontSize="small" />
                      Visual Guides
                    </Typography>
                  }
                />
                <Tooltip title="Show visual guides with screenshots when available">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Ask a question about how to use Narralyze..."
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendQuestion()}
                  size="small"
                />
                <Button
                  variant="contained"
                  onClick={handleSendQuestion}
                  disabled={!question.trim() || isLoading}
                  startIcon={<SendIcon />}
                >
                  Send
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default HelpDialog; 